import React from 'react';

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';

import cn from '~/utils/cn';

export interface TabItem {
  /**
   * Unique identifier for the tab
   */
  id: string;
  /**
   * Label text for the tab
   */
  label: string;
  /**
   * Content to render in the tab panel
   */
  content: React.ReactNode;
  /**
   * Whether the tab is disabled
   */
  disabled?: boolean;
}

export interface TabsProps {
  /**
   * Array of tab items
   */
  tabs: TabItem[];
  /**
   * Index of the initially selected tab
   */
  defaultIndex?: number;
  /**
   * Handler to call when the selected tab changes
   */
  onChange?: (index: number) => void;
  /**
   * Additional CSS classes to apply to the tabs container
   */
  className?: string;
  /**
   * Additional CSS classes to apply to the tab list
   */
  tabListClassName?: string;
  /**
   * Additional CSS classes to apply to individual tabs
   */
  tabClassName?: string;
  /**
   * Additional CSS classes to apply to the tab panels container
   */
  panelsClassName?: string;
  /**
   * Additional CSS classes to apply to individual tab panels
   */
  panelClassName?: string;
  /**
   * Whether the tabs should take full width
   */
  fullWidth?: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultIndex = 0,
  onChange,
  className = '',
  tabListClassName = '',
  tabClassName = '',
  panelsClassName = '',
  panelClassName = '',
  fullWidth = false,
}) => {
  const tabListStyles = cn(
    'flex border-b border-grey-200',
    {
      'w-full': fullWidth,
    },
    tabListClassName
  );

  const tabStyles = cn(
    'relative px-16 py-12 text-lg font-normal transition-all duration-200 focus:outline-none border-b-2 border-transparent',
    {
      'flex-1': fullWidth,
    },
    // Base styles
    'text-slate-800 hover:text-slate-900 hover:cursor-pointer hover:bg-current/4 rounded-t-lg',
    // Selected styles
    'data-selected:text-current data-selected:font-semibold data-selected:border-current',
    // Disabled styles
    'data-disabled:text-grey-400 data-disabled:cursor-not-allowed data-disabled:hover:text-grey-400',
    tabClassName
  );

  const panelsStyles = cn('text-slate-800', panelsClassName);

  const panelStyles = cn(panelClassName);

  return (
    <TabGroup
      defaultIndex={defaultIndex}
      onChange={onChange}
      className={cn('flex w-full flex-col text-blue-600', className)}
    >
      <TabList className={tabListStyles}>
        {tabs.map((tab) => (
          <Tab key={tab.id} disabled={tab.disabled} className={tabStyles}>
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <TabPanels className={panelsStyles}>
        {tabs.map((tab) => (
          <TabPanel key={tab.id} className={panelStyles}>
            {tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

export default Tabs;
